/**
 * Cienka obudowa na ECharts.
 *
 * Wykres jest tworzony raz i aktualizowany opcjami — przemontowanie instancji
 * przy każdej zmianie zakresu kasowałoby animację i migało. `ResizeObserver`
 * pilnuje rozmiaru, bo wykresy siedzą w siatce, która zmienia szerokość bez
 * zmiany rozmiaru okna.
 */

import { useEffect, useRef } from "react";
import type { EChartsOption } from "echarts";

import { echarts } from "../lib/charts";

type Props = {
  option: EChartsOption | null;
  height?: "short" | "default" | "tall";
  emptyLabel?: string;
  ariaLabel: string;
};

export function Chart({ option, height = "default", emptyLabel, ariaLabel }: Props) {
  const holder = useRef<HTMLDivElement>(null);
  const instance = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    if (!holder.current || !option) return;

    if (!instance.current) {
      instance.current = echarts.init(holder.current, undefined, { renderer: "canvas" });
    }
    instance.current.setOption(option, { notMerge: true });

    const observer = new ResizeObserver(() => instance.current?.resize());
    observer.observe(holder.current);
    return () => observer.disconnect();
  }, [option]);

  useEffect(() => {
    return () => {
      instance.current?.dispose();
      instance.current = null;
    };
  }, []);

  const className =
    height === "tall" ? "chart chart--tall" : height === "short" ? "chart chart--short" : "chart";

  if (!option) {
    return <div className="chart-empty">{emptyLabel ?? "Brak danych w tym zakresie."}</div>;
  }

  return <div ref={holder} className={className} role="img" aria-label={ariaLabel} />;
}
